import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Container,
  Title,
  Tabs,
  Card,
  Group,
  Text,
  Badge,
  Button,
  Grid,
  Code,
  Anchor,
  Stack,
  Loader,
  Center,
  Alert,
  Select,
  ActionIcon,
} from '@mantine/core';
import {
  IconArrowLeft,
  IconExternalLink,
  IconTrash,
  IconInfoCircle,
} from '@tabler/icons-react';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { api } from '@/services/api';
import { EnvironmentStatusBadge } from '@/components/EnvironmentStatusBadge';
import { DeleteEnvironmentModal } from '@/components/DeleteEnvironmentModal';
import { LogsViewer } from '@/components/LogsViewer';
import { ResourcesViewer } from '@/components/ResourcesViewer';
import { formatDateDistance, parseUTCDate } from '@/utils/date';
import { AutoRefreshControls } from '@/components/AutoRefreshControls';
import { useAutoRefresh } from '@/hooks/useAutoRefresh';

export function EnvironmentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<string | null>('overview');
  const [deleteModalOpened, deleteModalHandlers] = useDisclosure(false);
  const [extendTtl, setExtendTtl] = useState<string>('7d');
  const { enabled: autoRefresh, setEnabled: setAutoRefresh, refetchInterval } = useAutoRefresh({ 
    defaultEnabled: true, 
    interval: 30000 
  });

  // Safe date formatter helper (now UTC-aware)
  const safeFormatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return 'N/A';
    return formatDateDistance(dateString);
  };

  // Fetch environment details
  const {
    data: environment,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['environment', id],
    queryFn: () => api.getEnvironment(id!),
    enabled: !!id,
    refetchInterval, // Auto-refresh every 30s
  });

  // Extend TTL mutation
  const extendTtlMutation = useMutation({
    mutationFn: async (ttl: string) => {
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL || 'https://pk8s-api.aops.dev'}/api/environments/${id}`,
        {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ttl }),
        }
      );
      if (!response.ok) {
        throw new Error('Failed to extend TTL');
      }
      return response.json();
    },
    onSuccess: () => {
      notifications.show({
        title: 'Success',
        message: 'Environment TTL extended successfully',
        color: 'green',
      });
      queryClient.invalidateQueries({ queryKey: ['environment', id] });
    },
    onError: (error) => {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to extend TTL',
        color: 'red',
      });
    },
  });

  if (isLoading) {
    return (
      <Center h="50vh">
        <Loader size="lg" />
      </Center>
    );
  }

  if (error || !environment) {
    return (
      <Container size="lg" py="md">
        <Alert icon={<IconInfoCircle />} title="Error" color="red">
          {error instanceof Error ? error.message : 'Environment not found'}
        </Alert>
        <Button mt="md" onClick={() => navigate('/environments')}>
          Back to Environments
        </Button>
      </Container>
    );
  }

  return (
    <Container size="xl" py="md">
      <Stack gap="md">
        {/* Header */}
        <Group justify="space-between">
          <Group>
            <ActionIcon variant="subtle" onClick={() => navigate('/environments')}>
              <IconArrowLeft size={20} />
            </ActionIcon>
            <div>
              <Title order={2}>{environment.name}</Title>
              <Text size="sm" c="dimmed">
                {environment.namespace}
              </Text>
            </div>
            <EnvironmentStatusBadge status={environment.status} />
          </Group>
          <Group>
            <AutoRefreshControls
              enabled={autoRefresh}
              onEnabledChange={setAutoRefresh}
              intervalLabel="(30s)"
              onManualRefresh={refetch}
              isRefreshing={isLoading}
            />
            <Button
              color="red"
              leftSection={<IconTrash size={16} />}
              onClick={deleteModalHandlers.open}
            >
              Delete
            </Button>
          </Group>
        </Group>

        {/* Tabs */}
        <Tabs value={activeTab} onChange={setActiveTab}>
          <Tabs.List>
            <Tabs.Tab value="overview">Overview</Tabs.Tab>
            <Tabs.Tab value="logs">Deployment Logs</Tabs.Tab>
            <Tabs.Tab value="resources">Resources</Tabs.Tab>
            <Tabs.Tab value="settings">Settings</Tabs.Tab>
          </Tabs.List>

          {/* Overview Tab */}
          <Tabs.Panel value="overview" pt="md">
            <Grid>
              <Grid.Col span={{ base: 12, md: 6 }}>
                {/* Environment Info Card */}
                <Card withBorder>
                  <Stack gap="md">
                    <Text fw={500} size="lg">
                      Environment Information
                    </Text>
                    
                    <Stack gap="xs">
                      <Group justify="space-between">
                        <Text size="sm" c="dimmed">
                          ID
                        </Text>
                        <Code>{environment.id}</Code>
                      </Group>
                      
                      <Group justify="space-between">
                        <Text size="sm" c="dimmed">
                          Name
                        </Text>
                        <Text size="sm">{environment.name}</Text>
                      </Group>
                      
                      <Group justify="space-between">
                        <Text size="sm" c="dimmed">
                          Namespace
                        </Text>
                        <Code>{environment.namespace}</Code>
                      </Group>
                      
                      <Group justify="space-between">
                        <Text size="sm" c="dimmed">
                          Status
                        </Text>
                        <EnvironmentStatusBadge status={environment.status} />
                      </Group>
                      
                      <Group justify="space-between">
                        <Text size="sm" c="dimmed">
                          Created
                        </Text>
                        <Text size="sm">{safeFormatDate(environment.created_at)}</Text>
                      </Group>
                      
                      {environment.expires_at && (
                        <Group justify="space-between">
                          <Text size="sm" c="dimmed">
                            Expires
                          </Text>
                          <Badge
                            color={
                              (() => {
                                const expDate = parseUTCDate(environment.expires_at);
                                if (!expDate) return 'gray';
                                if (expDate < new Date()) return 'red';
                                if (expDate.getTime() - Date.now() < 24 * 60 * 60 * 1000) return 'yellow';
                                return 'gray';
                              })()
                            }
                            variant="light"
                          >
                            {safeFormatDate(environment.expires_at)}
                          </Badge>
                        </Group>
                      )}
                      
                      {environment.owner_github_login && (
                        <Group justify="space-between">
                          <Text size="sm" c="dimmed">
                            Owner
                          </Text>
                          <Text size="sm">{environment.owner_github_login}</Text>
                        </Group>
                      )}
                    </Stack>
                  </Stack>
                </Card>
              </Grid.Col>

              <Grid.Col span={{ base: 12, md: 6 }}>
                {/* Repository Config Card */}
                <Card withBorder>
                  <Stack gap="md">
                    <Text fw={500} size="lg">
                      Repository Configuration
                    </Text>
                    
                    <Stack gap="xs">
                      {environment.repoUrl && (
                        <Group justify="space-between">
                          <Text size="sm" c="dimmed">
                            Repository
                          </Text>
                          <Anchor
                            href={environment.repoUrl}
                            target="_blank"
                            size="sm"
                          >
                            <Group gap="xs">
                              {environment.repoUrl.split('/').slice(-2).join('/')}
                              <IconExternalLink size={14} />
                            </Group>
                          </Anchor>
                        </Group>
                      )}
                      
                      {environment.branch && (
                        <Group justify="space-between">
                          <Text size="sm" c="dimmed">
                            Branch
                          </Text>
                          <Code>{environment.branch}</Code>
                        </Group>
                      )}
                      
                      {environment.metadata?.appVersion && (
                        <Group justify="space-between">
                          <Text size="sm" c="dimmed">
                            App Version
                          </Text>
                          <Badge variant="light">{environment.metadata.appVersion}</Badge>
                        </Group>
                      )}
                      
                      {environment.metadata?.frontendDomain && (
                        <Group justify="space-between">
                          <Text size="sm" c="dimmed">
                            Frontend
                          </Text>
                          <Anchor
                            href={`https://${environment.metadata.frontendDomain}`}
                            target="_blank"
                            size="sm"
                          >
                            <Group gap="xs">
                              {environment.metadata.frontendDomain}
                              <IconExternalLink size={14} />
                            </Group>
                          </Anchor>
                        </Group>
                      )}
                      
                      {environment.metadata?.apiDomain && (
                        <Group justify="space-between">
                          <Text size="sm" c="dimmed">
                            API
                          </Text>
                          <Anchor
                            href={`https://${environment.metadata.apiDomain}`}
                            target="_blank"
                            size="sm"
                          >
                            <Group gap="xs">
                              {environment.metadata.apiDomain}
                              <IconExternalLink size={14} />
                            </Group>
                          </Anchor>
                        </Group>
                      )}

                      {environment.description && (
                        <>
                          <Text size="sm" c="dimmed" mt="sm">
                            Description
                          </Text>
                          <Text size="sm">{environment.description}</Text>
                        </>
                      )}
                    </Stack>
                  </Stack>
                </Card>
              </Grid.Col>

              <Grid.Col span={12}>
                {/* Actions Card */}
                <Card withBorder>
                  <Group justify="space-between">
                    <Text fw={500} size="lg">
                      Quick Actions
                    </Text>
                    <Group>
                      {environment.metadata?.frontendDomain && (
                        <Button
                          component="a"
                          href={`https://${environment.metadata.frontendDomain}`}
                          target="_blank"
                          rightSection={<IconExternalLink size={16} />}
                        >
                          Open Frontend
                        </Button>
                      )}
                      {environment.metadata?.apiDomain && (
                        <Button
                          component="a"
                          href={`https://${environment.metadata.apiDomain}`}
                          target="_blank"
                          variant="light"
                          rightSection={<IconExternalLink size={16} />}
                        >
                          Open API
                        </Button>
                      )}
                    </Group>
                  </Group>
                </Card>
              </Grid.Col>
            </Grid>
          </Tabs.Panel>

          {/* Logs Tab */}
          <Tabs.Panel value="logs" pt="md">
            <LogsViewer environmentId={id!} />
          </Tabs.Panel>

          {/* Resources Tab */}
          <Tabs.Panel value="resources" pt="md">
            <ResourcesViewer environmentId={id!} />
          </Tabs.Panel>

          {/* Settings Tab */}
          <Tabs.Panel value="settings" pt="md">
            <Grid>
              <Grid.Col span={{ base: 12, md: 6 }}>
                {/* Extend TTL Card */}
                <Card withBorder>
                  <Stack gap="md">
                    <Text fw={500} size="lg">
                      Time to Live (TTL)
                    </Text>
                    
                    <Stack gap="xs">
                      <Group justify="space-between">
                        <Text size="sm" c="dimmed">
                          Current Expiry
                        </Text>
                        {environment.expires_at ? (
                          <Badge
                            color={
                              (() => {
                                const expDate = parseUTCDate(environment.expires_at);
                                if (!expDate) return 'gray';
                                if (expDate < new Date()) return 'red';
                                if (expDate.getTime() - Date.now() < 24 * 60 * 60 * 1000) return 'yellow';
                                return 'gray';
                              })()
                            }
                          >
                            {safeFormatDate(environment.expires_at)}
                          </Badge>
                        ) : (
                          <Text size="sm">Never</Text>
                        )}
                      </Group>
                      
                      <Group mt="sm">
                        <Select
                          label="Extend TTL by"
                          value={extendTtl}
                          onChange={(value) => setExtendTtl(value || '7d')}
                          data={[
                            { value: '1h', label: '1 hour' },
                            { value: '4h', label: '4 hours' },
                            { value: '24h', label: '24 hours' },
                            { value: '7d', label: '7 days' },
                            { value: '30d', label: '30 days' },
                          ]}
                          style={{ flex: 1 }}
                        />
                        <Button
                          mt="xl"
                          onClick={() => extendTtlMutation.mutate(extendTtl)}
                          loading={extendTtlMutation.isPending}
                        >
                          Extend
                        </Button>
                      </Group>
                    </Stack>
                  </Stack>
                </Card>
              </Grid.Col>

              <Grid.Col span={{ base: 12, md: 6 }}>
                {/* Metadata Card */}
                <Card withBorder>
                  <Stack gap="md">
                    <Text fw={500} size="lg">
                      Metadata
                    </Text>
                    
                    <Stack gap="xs">
                      <Group justify="space-between">
                        <Text size="sm" c="dimmed">
                          Created
                        </Text>
                        <Text size="sm">{safeFormatDate(environment.created_at)}</Text>
                      </Group>
                      
                      {environment.updated_at && (
                        <Group justify="space-between">
                          <Text size="sm" c="dimmed">
                            Updated
                          </Text>
                          <Text size="sm">{safeFormatDate(environment.updated_at)}</Text>
                        </Group>
                      )}
                      
                      <Group justify="space-between">
                        <Text size="sm" c="dimmed">
                          ID
                        </Text>
                        <Code>{environment.id}</Code>
                      </Group>
                      
                      <Group justify="space-between">
                        <Text size="sm" c="dimmed">
                          Namespace
                        </Text>
                        <Code>{environment.namespace}</Code>
                      </Group>
                      
                      {environment.metadata && Object.keys(environment.metadata).length > 0 && (
                        <>
                          <Text size="sm" c="dimmed" mt="sm">
                            Additional Metadata
                          </Text>
                          <Stack gap="xs">
                            {Object.entries(environment.metadata).map(([key, value]) => (
                              <Group key={key} justify="space-between">
                                <Text size="sm" c="dimmed">
                                  {key}
                                </Text>
                                <Code>{typeof value === 'object' ? JSON.stringify(value) : String(value)}</Code>
                              </Group>
                            ))}
                          </Stack>
                        </>
                      )}
                    </Stack>
                  </Stack>
                </Card>
              </Grid.Col>
            </Grid>
          </Tabs.Panel>
        </Tabs>
      </Stack>

      <DeleteEnvironmentModal
        environment={environment}
        opened={deleteModalOpened}
        onClose={() => {
          deleteModalHandlers.close();
          navigate('/environments');
        }}
      />
    </Container>
  );
}

